import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import AcademiaSelect from '../../components/common/AcademiaSelect';
import LucideSymbolIcon from '../../components/icons/LucideSymbolIcon';
import PublicSiteHeader from '../../components/public/PublicSiteHeader';
import PublicReveal, { publicRevealStagger } from '../../components/public/PublicReveal';
import PublicSiteFooter from '../../components/public/PublicSiteFooter';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import { fetchPublicExcellence } from '../../services/academiaAdminService';
import { buildDefaultPeriodeOptions, formatPeriodeLabel } from '../../utils/excellencePeriodes';

type ClassementRow = {
  rang: number | null;
  nomComplet: string;
  matricule: string;
  scoreGlobal: number;
  scoreAcademique?: number;
  scorePresence?: number;
  scoreDiscipline?: number;
  scoreImplicationPedagogique?: number;
  scorePratique?: number;
  scoreProjets?: number;
};

type ExcellenceData = {
  periode: string;
  periodes?: string[];
  ponderation?: Record<string, number>;
  classement: ClassementRow[];
  partenaires?: { nom: string; typePartenaire: string }[];
  opportunites?: { titre: string; typeLabel: string; partenaire: string | null; reserveShortlist?: boolean }[];
  typesAvantages?: { value: string; label: string }[];
};

const CRITERIA_META = [
  { key: 'academique', label: 'Académique', icon: 'school' as const },
  { key: 'presence', label: 'Présence', icon: 'event_available' as const },
  { key: 'discipline', label: 'Discipline', icon: 'gavel' as const },
  { key: 'implicationPedagogique', label: 'Implication', icon: 'groups' as const },
  { key: 'pratique', label: 'Pratique', icon: 'build' as const },
  { key: 'projets', label: 'Projets', icon: 'folder_special' as const },
];

const PODIUM_STYLES: Record<number, { ring: string; bg: string; height: string; medal: string }> = {
  1: {
    ring: 'ring-amber-300/80',
    bg: 'bg-gradient-to-b from-amber-50 to-white',
    height: 'min-h-[11rem]',
    medal: 'text-amber-500',
  },
  2: {
    ring: 'ring-slate-300/80',
    bg: 'bg-gradient-to-b from-slate-50 to-white',
    height: 'min-h-[9.5rem]',
    medal: 'text-slate-500',
  },
  3: {
    ring: 'ring-orange-300/70',
    bg: 'bg-gradient-to-b from-orange-50 to-white',
    height: 'min-h-[8.5rem]',
    medal: 'text-orange-600',
  },
};

function initials(name: string) {
  return (
    name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  );
}

function PodiumCard({ row }: { row: ClassementRow }) {
  const rank = row.rang ?? 0;
  const style = PODIUM_STYLES[rank];
  if (!style) return null;

  return (
    <article
      className={`flex flex-col items-center rounded-public-card border border-white/60 p-4 text-center shadow-lg ring-2 ${style.ring} ${style.bg} ${style.height} ${rank === 1 ? 'order-2 sm:scale-105' : rank === 2 ? 'order-1' : 'order-3'}`}
    >
      <LucideSymbolIcon name="emoji_events" tone="inherit" size={rank === 1 ? 28 : 22} className={style.medal} />
      <span className="mt-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-sm font-black text-primary">
        {initials(row.nomComplet)}
      </span>
      <p className="mt-3 line-clamp-2 text-sm font-extrabold text-slate-900">{row.nomComplet}</p>
      <p className="mt-1 font-mono text-[11px] text-on-surface-variant">{row.matricule}</p>
      <p className="mt-3 text-2xl font-black text-primary">{row.scoreGlobal}</p>
      <p className="text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">Rang #{rank}</p>
    </article>
  );
}

type Props = {
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  spaceLabel?: string;
  onGoToSpace?: () => void;
};

export default function PublicExcellencePage({
  isAuthenticated,
  isAdmin,
  spaceLabel,
  onGoToSpace,
}: Props) {
  const [periode, setPeriode] = useState(() => `${new Date().getFullYear()}-S1`);
  const [periodeOptions, setPeriodeOptions] = useState<string[]>(buildDefaultPeriodeOptions);
  const [reloadKey, setReloadKey] = useState(0);
  const [data, setData] = useState<ExcellenceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const row = (await fetchPublicExcellence(periode)) as ExcellenceData;
        if (!cancelled) {
          if (row.periodes?.length) {
            setPeriodeOptions(row.periodes);
            if (!row.periodes.includes(periode)) {
              setPeriode(row.periode);
            }
          }
          setData(row);
        }
      } catch {
        if (!cancelled) {
          setData(null);
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [periode, reloadKey]);

  const { podium, others, tableRows } = useMemo(() => {
    const list = data?.classement ?? [];
    const top = list.filter((r) => r.rang != null && r.rang <= 3).sort((a, b) => (a.rang ?? 0) - (b.rang ?? 0));
    const rest = list.filter((r) => r.rang == null || r.rang > 3);
    const rows = rest.length > 0 ? rest : top.length === 0 ? list : [];
    return { podium: top, others: rest, tableRows: rows };
  }, [data?.classement]);

  const ponderation = data?.ponderation;

  return (
    <div className="min-h-screen bg-[#f9f9ff] text-on-surface">
      <PublicSiteHeader
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        spaceLabel={spaceLabel}
        onGoToSpace={onGoToSpace}
      />

      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-[#6b1b18] to-slate-900 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.15) 0%, transparent 45%), radial-gradient(circle at 80% 0%, rgba(255,213,211,0.2) 0%, transparent 40%)',
          }}
        />
        <div className="relative mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
          <PublicReveal animation="fade-in-left">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/85 transition hover:text-white"
          >
            <LucideSymbolIcon name="arrow_back" tone="inherit" size={16} />
            Accueil JCONSULT MY Academia
          </Link>
          <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Tableau des excellences</h1>
              <p className="mt-4 text-sm leading-relaxed text-white/85 sm:text-base">
                Classement public des meilleurs apprenants (shortlist) : résultats académiques, présence,
                discipline, implication, pratique et projets — orientations employabilité chez nos partenaires.
              </p>
            </div>
            <div className="w-full max-w-xs rounded-public-card border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
              <label htmlFor="excellence-periode" className="block text-xs font-bold uppercase tracking-wide text-white/80">
                Période
              </label>
              <AcademiaSelect
                id="excellence-periode"
                className="academia-field mt-2 w-full border-white/30 bg-white/95 text-slate-900"
                value={periode}
                onChange={(e) => setPeriode(e.target.value)}
                aria-label="Période académique"
              >
                {periodeOptions.map((p) => (
                  <option key={p} value={p}>
                    {formatPeriodeLabel(p)}
                  </option>
                ))}
              </AcademiaSelect>
            </div>
          </div>
          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            <PublicReveal animation="fade-in-up" delay={publicRevealStagger(0, 100, 200)}>
            <div className="rounded-public-card border border-white/15 bg-white/10 px-4 py-3 backdrop-blur-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-white/70">Apprenants shortlist</p>
              <p className="mt-1 text-2xl font-black">{loading ? '…' : (data?.classement.length ?? 0)}</p>
            </div>
            </PublicReveal>
            <PublicReveal animation="fade-in-up" delay={publicRevealStagger(1, 100, 200)}>
            <div className="rounded-public-card border border-white/15 bg-white/10 px-4 py-3 backdrop-blur-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-white/70">Partenaires</p>
              <p className="mt-1 text-2xl font-black">{loading ? '…' : (data?.partenaires?.length ?? 0)}</p>
            </div>
            </PublicReveal>
            <PublicReveal animation="fade-in-up" delay={publicRevealStagger(2, 100, 200)}>
            <div className="rounded-public-card border border-white/15 bg-white/10 px-4 py-3 backdrop-blur-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-white/70">Opportunités</p>
              <p className="mt-1 text-2xl font-black">{loading ? '…' : (data?.opportunites?.length ?? 0)}</p>
            </div>
            </PublicReveal>
          </div>
          </PublicReveal>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
        {ponderation ? (
          <PublicReveal animation="fade-in-up">
          <section className="rounded-public-card border border-outline-variant/25 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-primary">Méthodologie</p>
                <h2 className="mt-1 text-xl font-black text-slate-900">Critères de classement</h2>
              </div>
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                Période {data?.periode ?? periode}
              </span>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {CRITERIA_META.map((c) => {
                const weight = ponderation[c.key];
                if (weight == null) return null;
                return (
                  <article
                    key={c.key}
                    className="flex items-center gap-3 rounded-public-card border border-outline-variant/20 bg-gradient-to-r from-primary/5 to-transparent px-4 py-3"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <LucideSymbolIcon name={c.icon} tone="primary" size={20} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-slate-900">{c.label}</p>
                      <p className="text-xs text-on-surface-variant">Pondération officielle</p>
                    </div>
                    <span className="text-lg font-black text-primary">{(weight * 100).toFixed(0)}%</span>
                  </article>
                );
              })}
            </div>
          </section>
          </PublicReveal>
        ) : null}

        <section className="mt-10">
          <PublicReveal animation="fade-in-up">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-widest text-primary">Classement</p>
            <h2 className="mt-2 text-2xl font-black text-slate-900 sm:text-3xl">Shortlist excellence</h2>
            <p className="mt-2 text-sm text-on-surface-variant">
              Les profils les mieux classés accèdent aux avantages employabilité : stages, emploi, mentorat, bourses
              et formations avancées.
            </p>
          </div>
          </PublicReveal>

          {loading ? (
            <div className="mt-10 flex justify-center py-16">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
            </div>
          ) : error ? (
            <div className="mt-8 rounded-public-card border border-red-200 bg-red-50 px-6 py-10 text-center">
              <p className="font-semibold text-red-900">Impossible de charger le classement.</p>
              <button
                type="button"
                className="mt-4 rounded-public-btn bg-primary px-5 py-2.5 text-sm font-bold text-white"
                onClick={() => setReloadKey((k) => k + 1)}
              >
                Réessayer
              </button>
            </div>
          ) : (data?.classement ?? []).length === 0 ? (
            <div className="mt-8 rounded-public-card border border-dashed border-outline-variant/40 bg-white px-6 py-14 text-center">
              <LucideSymbolIcon name="emoji_events" tone="muted" size={40} className="mx-auto opacity-50" />
              <p className="mt-4 font-semibold text-slate-800">Aucun résultat pour cette période</p>
              <p className="mt-2 text-sm text-on-surface-variant">
                Le classement est publié après la délibération académique.
              </p>
            </div>
          ) : (
            <>
              {podium.length > 0 ? (
                <div className="mt-8 grid grid-cols-1 items-end gap-4 sm:grid-cols-3">
                  {podium.map((row, index) => (
                    <PublicReveal key={`podium-${row.rang}-${row.matricule}`} animation="fade-in-up" delay={publicRevealStagger(index, 120)}>
                    <PodiumCard row={row} />
                    </PublicReveal>
                  ))}
                </div>
              ) : null}

              {tableRows.length > 0 ? (
              <div className="mt-8 overflow-hidden rounded-public-card border border-outline-variant/25 bg-white shadow-sm">
                {others.length > 0 ? (
                  <p className="border-b border-outline-variant/15 bg-slate-50 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                    Suite du classement
                  </p>
                ) : null}
                <div className="hidden grid-cols-[4rem_1fr_6rem_5rem] gap-4 border-b border-outline-variant/20 bg-slate-50 px-5 py-3 text-xs font-bold uppercase tracking-wide text-on-surface-variant sm:grid">
                  <span>Rang</span>
                  <span>Apprenant</span>
                  <span>Matricule</span>
                  <span className="text-right">Score</span>
                </div>
                <ol className="divide-y divide-outline-variant/15">
                  {tableRows.map((row) => (
                    <li
                      key={`${row.rang}-${row.matricule}`}
                      className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 transition hover:bg-primary/[0.03] sm:grid sm:grid-cols-[4rem_1fr_6rem_5rem] sm:gap-4"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-black text-primary">
                        {row.rang ?? '—'}
                      </span>
                      <div className="min-w-0 flex-1 sm:col-span-1">
                        <p className="truncate font-bold text-slate-900">{row.nomComplet}</p>
                        {row.rang != null && row.rang <= 3 ? (
                          <p className="text-[11px] font-semibold text-amber-700">Top 3 shortlist</p>
                        ) : null}
                      </div>
                      <span className="font-mono text-xs text-on-surface-variant sm:text-sm">{row.matricule}</span>
                      <span className="text-lg font-black text-slate-900 sm:text-right">{row.scoreGlobal}</span>
                    </li>
                  ))}
                </ol>
              </div>
              ) : null}
            </>
          )}
        </section>

        {(data?.partenaires ?? []).length > 0 ? (
          <section className="mt-14">
            <p className="text-xs font-bold uppercase tracking-widest text-primary">Réseau</p>
            <h2 className="mt-2 text-2xl font-black text-slate-900">Partenaires employabilité</h2>
            <p className="mt-2 max-w-2xl text-sm text-on-surface-variant">
              Institutions financières, télécoms, startups et employeurs orientent les meilleurs profils vers des
              opportunités concrètes.
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data!.partenaires!.map((p) => (
                <li
                  key={p.nom}
                  className="flex items-center gap-3 rounded-public-card border border-outline-variant/25 bg-white px-4 py-3 shadow-sm"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <LucideSymbolIcon name="handshake" tone="primary" size={20} />
                  </span>
                  <div>
                    <p className="font-bold text-slate-900">{p.nom}</p>
                    <p className="text-xs text-on-surface-variant">{p.typePartenaire.replace(/_/g, ' ')}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {(data?.opportunites ?? []).length > 0 ? (
          <section className="mt-14">
            <p className="text-xs font-bold uppercase tracking-widest text-primary">Avantages</p>
            <h2 className="mt-2 text-2xl font-black text-slate-900">Opportunités shortlist</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {data!.opportunites!.map((o) => (
                <article
                  key={o.titre}
                  className="overflow-hidden rounded-public-card border border-outline-variant/25 bg-white shadow-sm transition hover:border-primary/30 hover:shadow-md"
                >
                  <div className="border-b border-outline-variant/15 bg-gradient-to-r from-primary/5 to-transparent px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-bold text-primary">
                        {o.typeLabel}
                      </span>
                      {o.reserveShortlist ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-900">
                          Shortlist
                        </span>
                      ) : null}
                    </div>
                    <h3 className="mt-2 font-extrabold text-slate-900">{o.titre}</h3>
                  </div>
                  {o.partenaire ? (
                    <p className="px-4 py-3 text-xs font-semibold text-on-surface-variant">
                      <LucideSymbolIcon name="handshake" tone="muted" size={14} className="mr-1 inline-block" />
                      {o.partenaire}
                    </p>
                  ) : null}
                </article>
              ))}
            </div>
          </section>
        ) : null}

        <div className="mt-14 rounded-public-card border border-primary/20 bg-gradient-to-r from-primary/10 via-white to-primary/5 px-6 py-8 text-center">
          <p className="text-lg font-black text-slate-900">Vous êtes apprenant JCONSULT MY Academia ?</p>
          <p className="mt-2 text-sm text-on-surface-variant">
            Consultez votre rang, vos critères et vos orientations dans votre espace personnel.
          </p>
          <Link
            to="/login"
            className="mt-5 inline-flex items-center gap-2 rounded-public-btn bg-primary px-6 py-3 text-sm font-bold text-white shadow-md transition hover:bg-primary-dark"
          >
            <LucideSymbolIcon name="login" tone="inherit" size={18} />
            Se connecter
          </Link>
        </div>
      </main>

      <PublicSiteFooter showHomeLink />

      <ScrollToTopButton />
    </div>
  );
}
